import { useState } from 'react';

import {
	LockOpenOutlined as LockOpenOutlinedIcon,
	LockOutlined as LockOutlinedIcon,
	Refresh as RefreshIcon,
} from '@mui/icons-material';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import { Typography, Hidden, Stack, Button } from '@mui/material';
import classNames from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import BatteryUnlockIcon from '~assets/icons/ic-battery-unlock.svg?react';
import { useAuthorize } from '~features/authentication';
import { SkcDevicesService } from '~features/devices';
import { useSnackbar } from '~hooks';

import { useStyles } from './style';
import { ConfirmationDialog, AdjustedSkeleton, StyledButton } from '../../../../components';

interface HeaderProps {
	className?: string;
	deviceData?: object;
	loading?: boolean;
}

const skcDevicesServices = new SkcDevicesService();

const Header = (props: HeaderProps) => {
	const { isSuperAdmin } = useAuthorize();
	const { className, deviceData, loading } = props;
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();

	const [commandType, setCommandType] = useState<string | null>(null);
	const [commandProcessing, setCommandProcessing] = useState(false);

	const classes = useStyles();

	const { data } = useSWR(
		deviceData?.deviceId != null ?
			[skcDevicesServices.basePath, { hardwareIds: [deviceData?.deviceId] }]
		:	null,
		([_, args]) => skcDevicesServices.getDevices(args)
	);

	const showServerCommands = deviceData?.communicationType?.includes('lte');

	const createCommandsFilterArray = () => {
		const filterList = ['unlock', 'lock', 'batteryUnlock'];
		if (isSuperAdmin()) {
			filterList.push('reboot');
		}
		return filterList;
	};

	const labelForCommand = (command: string) => {
		switch (command) {
			case 'unlock':
				return t('unlock');
			case 'lock':
				return t('lock');
			case 'batteryUnlock':
				return t('batteryUnlock');
			case 'reboot':
				return t('reset');
			default:
				return command;
		}
	};

	const iconForCommand = (command: string) => {
		switch (command) {
			case 'unlock':
				return <LockOpenOutlinedIcon />;
			case 'lock':
				return <LockOutlinedIcon />;
			case 'batteryUnlock':
				return <BatteryUnlockIcon />;
			case 'reboot':
				return <RefreshIcon />;
			default:
				return null;
		}
	};

	const { data: commands, isLoading: isCommandsLoading } = useSWR(
		showServerCommands && deviceData?.deviceId != null ?
			[
				skcDevicesServices.basePath,
				skcDevicesServices.commandsSubPath,
				skcDevicesServices.lteCommandsSubPath,
				deviceData.deviceId,
			]
		:	null,
		([_, __, ___, deviceId]) =>
			skcDevicesServices.getDeviceCommands(deviceId, createCommandsFilterArray())
	);
	const availableCommands = Array.isArray(commands) ? commands : [];

	const executeCommand = async (command: string) => {
		const hardwareId = deviceData?.deviceId;
		if (!hardwareId) {
			enqueueErrorSnackbar(t('somethingWentWrong'));
			return;
		}

		setCommandProcessing(true);

		try {
			await skcDevicesServices.postCommand(hardwareId, command);
			enqueueSuccessSnackbar(
				t('actionMessageWasSendToResource', {
					action: labelForCommand(command),
					resource: t('device'),
				})
			);
		} catch (_error) {
			enqueueErrorSnackbar(t('somethingWentWrong'));
		} finally {
			setCommandProcessing(false);
			setCommandType(null);
		}
	};

	const handleConfirmClick = () => {
		if (!commandType) {
			enqueueErrorSnackbar(t('somethingWentWrong'));
			return;
		}

		executeCommand(commandType);
	};

	const handleBackButtonChange = () => {
		if (location?.state?.backPathname) {
			navigate(location.state.backPathname);
		} else {
			if (location?.state?.from) {
				navigate(location.state.from);
			} else {
				navigate('/devices/assigned');
			}
		}
		sessionStorage.removeItem('pageNumberHeartbeats');
		sessionStorage.removeItem('pageSizeHeartbeats');
	};

	return (
		<div className={classNames(classes.root, className)}>
			<Typography component='h2' gutterBottom variant='overline'>
				<Hidden only={['xs', 'sm']}>
					<StyledButton
						className={classes.backButton}
						onClick={handleBackButtonChange}
						startIcon={<KeyboardArrowLeftOutlinedIcon />}
						variant='inline-default'
					>
						{t('ui.back')}
					</StyledButton>
				</Hidden>
			</Typography>
			<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
				<Typography component='h1' variant='h3'>
					{loading ?
						<AdjustedSkeleton animation='wave' width={200} />
					:	`HW UID: ${deviceData.deviceId}`}
				</Typography>
				{showServerCommands ?
					<Stack alignItems='flex-end' spacing={1}>
						{isCommandsLoading ?
							<AdjustedSkeleton animation='wave' width={200} />
						:	<Stack direction='row' spacing={1}>
								{availableCommands.map((command, index) => (
									<Button
										key={`${command}-${index}`}
										loading={commandProcessing && commandType === command}
										onClick={() => setCommandType(command)}
										variant='text'
										disabled={commandProcessing && commandType !== command}
										startIcon={iconForCommand(command)}
									>
										{labelForCommand(command)}
									</Button>
								))}
							</Stack>
						}
					</Stack>
				:	null}
			</Stack>
			<ConfirmationDialog
				title={commandType != null ? labelForCommand(commandType) : undefined}
				subTitle={
					commandType != null ?
						t('pleaseConfirmActionForResource', {
							action: labelForCommand(commandType),
							resource: t('device'),
						})
					:	null
				}
				open={commandType != null && !commandProcessing}
				onConfirm={handleConfirmClick}
				onClose={() => setCommandType(null)}
			/>
		</div>
	);
};

export default Header;
